import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Banknote, CheckCircle2 } from 'lucide-react';
import { loanProfilingApi } from '@/services/api';
import { SectionHeading, inr } from './CockpitUI';
import { OfferDisbursement } from './OfferDisbursement';

/** One car, one customer, one disbursal — so it gets a step of its own. */
export const DisbursementStep = ({ leadId, onChanged }) => {
  const [state, setState] = useState({ offers: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await loanProfilingApi.listOffers(leadId);
      setState(res.data);
    } catch (err) {
      toast.error('Could not load the disbursement');
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const apply = (data) => {
    setState(data);
    if (onChanged) onChanged();
  };

  if (loading) {
    return <div className="py-16 flex justify-center" data-testid="disbursement-loading"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  const offers = state.offers || [];
  const chosen = offers.find((o) => o.status === 'DISBURSED') || offers.find((o) => o.status === 'ACCEPTED');
  const done = chosen?.status === 'DISBURSED';

  return (
    <div data-testid="disbursement-tab">
      <div className="flex items-start justify-between gap-4 mb-4">
        <SectionHeading hint="The bank's release order and what it actually paid out — read from the order and checked against the offer the customer accepted.">
          Disbursement
        </SectionHeading>
        {done && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1"
            data-testid="disbursement-done-badge">
            <CheckCircle2 className="w-4 h-4" />Disbursed by {chosen.bank_name}
          </span>
        )}
      </div>

      {!chosen ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="disbursement-empty">
          <Banknote className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-700">No bank has been chosen yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
            Record the customer's choice on Offers Letter first. Once a bank is confirmed, upload its release order
            here — there is only ever one disbursal for one car.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 mb-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm text-slate-600"
            data-testid="disbursement-offer-summary">
            <span><strong className="text-slate-900">{chosen.bank_name}</strong> — the bank the customer chose</span>
            <span className="tabular-nums">{chosen.roi ? `${chosen.roi}%` : '—'} · {chosen.tenure_months ? `${chosen.tenure_months} months` : '—'} · EMI {inr(chosen.emi)}</span>
            <span>Offered net <strong className="text-slate-800">{inr(chosen.net_disbursement)}</strong></span>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <OfferDisbursement leadId={leadId} offer={chosen} onSaved={apply} />
          </div>
        </>
      )}
    </div>
  );
};

export default DisbursementStep;
